import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

interface FilterSection {
  title: string;
  isExpanded: boolean;
}

const FilterSidebar: React.FC = () => {
  const [sections, setSections] = useState({
    region: true,
    campsiteType: true,
    facilities: true,
    occupancyLevel: true,
    accessibility: true
  });

  const toggleSection = (section: keyof typeof sections) => {
    setSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const FilterSection: React.FC<{
    title: string;
    isExpanded: boolean;
    onToggle: () => void;
    children: React.ReactNode;
  }> = ({ title, isExpanded, onToggle, children }) => (
    <div className="border-b border-gray-200 pb-4 mb-4">
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full mb-3"
      >
        <h3
          style={{
            fontFamily: 'Poppins',
            fontSize: '16px',
            fontWeight: 600
          }}
        >
          {title}
        </h3>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>
      {isExpanded && <div className="space-y-3">{children}</div>}
    </div>
  );

  const CheckboxItem: React.FC<{
    label: string;
    defaultChecked?: boolean;
    color?: string;
  }> = ({ label, defaultChecked, color }) => (
    <div className="flex items-center space-x-2">
      <Checkbox id={label} defaultChecked={defaultChecked} />
      {color && (
        <div
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: color }}
        />
      )}
      <label
        htmlFor={label}
        className="text-sm cursor-pointer"
        style={{
          fontFamily: 'Inter',
          fontSize: '14px',
          fontWeight: 400
        }}
      >
        {label}
      </label>
    </div>
  );

  return (
    <div className="w-[280px] bg-white border border-gray-200 rounded-2xl p-6">
      <h2
        className="mb-6"
        style={{
          fontFamily: 'Poppins',
          fontSize: '24px',
          fontWeight: 700
        }}
      >
        Filter
      </h2>

      {/* Region */}
      <FilterSection
        title="Region"
        isExpanded={sections.region}
        onToggle={() => toggleSection('region')}
      >
        <CheckboxItem label="Hong Kong Island" />
        <CheckboxItem label="Kowloon" />
        <CheckboxItem label="New Territories" defaultChecked />
        <CheckboxItem label="Outlying Islands" />
      </FilterSection>

      {/* Campsite Type */}
      <FilterSection
        title="Campsite Type"
        isExpanded={sections.campsiteType}
        onToggle={() => toggleSection('campsiteType')}
      >
        <CheckboxItem label="Tent Camping Site" />
        <CheckboxItem label="BBQ Site" />
        <CheckboxItem label="Picnic Site" />
        <CheckboxItem label="Caravan / Car Camping" />
        <CheckboxItem label="Glamping Area" />
      </FilterSection>

      {/* Facilities */}
      <FilterSection
        title="Facilities"
        isExpanded={sections.facilities}
        onToggle={() => toggleSection('facilities')}
      >
        <CheckboxItem label="Restrooms" />
        <CheckboxItem label="Showers" />
        <CheckboxItem label="On-site Shop" />
        <CheckboxItem label="BBQ Pits" />
        <CheckboxItem label="Picnic Tables" />
        <CheckboxItem label="Parking" />
        <CheckboxItem label="Pet Friendly" />
      </FilterSection>

      {/* Occupancy Level */}
      <FilterSection
        title="Occupancy Level"
        isExpanded={sections.occupancyLevel}
        onToggle={() => toggleSection('occupancyLevel')}
      >
        <CheckboxItem label="Plenty of Space" color="#719C40" />
        <CheckboxItem label="Comfortably Busy" color="#FFB230" />
        <CheckboxItem label="Getting Crowded" color="#FF6B35" />
        <CheckboxItem label="Almost Full" color="#F53743" />
        <CheckboxItem label="Fully Occupied" color="#000000" />
      </FilterSection>

      {/* Accessibility */}
      <FilterSection
        title="Accessibility"
        isExpanded={sections.accessibility}
        onToggle={() => toggleSection('accessibility')}
      >
        <CheckboxItem label="Near MTR Station" />
        <CheckboxItem label="Parking Available" />
        <CheckboxItem label="Shuttle Bus Access" />
        <CheckboxItem label="Hiking Access Only" />
      </FilterSection>
    </div>
  );
};

export default FilterSidebar;
